import React from "react";
import styled from "styled-components/macro";
import colors from "../theme/colors";
import IconButton from "./IconButton";
import useDetectClickOutside from "../hooks/useDetectClickOutside";
import media from "../theme/media";

type MagnifyProps = {
  textScaleHandler: (output: boolean) => void;
};

const Magnify: React.FC<MagnifyProps> = ({
  textScaleHandler
}) => {
  const [isMagnifyOpen, setIsMagnifyOpen] = React.useState<boolean>(false);
  const magnifyRef = React.useRef<HTMLDivElement>(null);

  useDetectClickOutside(magnifyRef, () => setIsMagnifyOpen(false));

  return (
    <MagnifyWrapper
      ref={magnifyRef}
      isOpen={isMagnifyOpen}
    >
      <IconButton
        icon="search"
        label="Change Text Size"
        onClick={() => {setIsMagnifyOpen(!isMagnifyOpen)}}
      />

      <MagnifyContentWrapper isOpen={isMagnifyOpen}>
        <IconButton
          icon="plus"
          label="Increase"
          labelHidden={true}
          onClick={() => textScaleHandler(true)}
        />

        <MagnifyButtonDivider>/</MagnifyButtonDivider>

        <IconButton
          icon="minus"
          label="Decrease"
          labelHidden={true}
          onClick={() => textScaleHandler(false)}
        />
      </MagnifyContentWrapper>
    </MagnifyWrapper>
  );
};

export default Magnify;

const MagnifyWrapper = styled.div<{ isOpen?: boolean; }>`
  display: none;
  position: relative;
  padding: 5px;
  max-width: 110px;
  background-color: ${({ isOpen }) => (isOpen ? `${colors.lightBlue}` : `transparent`)};
  transition: background-color 0.2s;

  ${media.greaterThan("phone")`
    display: flex;
  `}

  // Only target the root level icon button
  // The alert bar doesn't scale, so need to set font-size in px instead of rem
  button {
    font-size: 14px;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;

    .icon-container {
      margin-right: 0;
      margin-bottom: 3px;
    }
  }
`;

const MagnifyContentWrapper = styled.div<{ isOpen?: boolean; }>`
  position: absolute;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-evenly;
  align-items: center;
  top: 100%;
  left: 0;
  width: 100%;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background: ${colors.lightBlue};
  overflow: hidden;
  transform-origin: top;
  transition: height 0.5s, transform 0.5s;
  height: ${({ isOpen }) => (isOpen ? `50px` : `0`)};
  transform: scaleY(${({ isOpen }) => (isOpen ? `1` : `0`)});

  button {
    margin: 5px;
    // The alert bar doesn't scale, so need to set font-size in px instead of rem
    font-size: 30px;
  }
`;

const MagnifyButtonDivider = styled.span`
  position: relative;
  left: -2px;
  color: ${colors.blue};
`;
